import { ApolloConsumer } from '@apollo/client';
import { getKeySetSize } from '@tableau/data-structures';
import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import {
  cssClass,
  DataCatalogApolloClient,
  Style,
  SingleSelectionProviderChildProps
} from '../../../data-catalog-shared';
import { buttonStyle, margin } from './MultiSelectionControls';
import { SmallInlineFadeInSpinner } from './SmallInlineFadeInSpinner';

export const containerClass = cssClass('SingleSelectionControls_container', {
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const dividerClass = cssClass('SingleSelectionControls_divider', {
  ...margin,
  display: 'inline-block',
  height: 20,
  width: 1,
  backgroundColor: TabStyles.Colors.L3,
} as Style);

const textClass = cssClass('SingleSelectionControls_text', {
  ...margin,
  fontSize: TabStyles.Typography.Sizing.Default,
  color: TabStyles.Colors.L9
} as Style);

const clearAndCountClass = cssClass('SingleSelectionControls_clearAndCount', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
} as Style);


export type SingleSelectionControlsProps = SingleSelectionProviderChildProps & {
  readonly clearSelection?: () => void;
};

export class SingleSelectionControls extends React.PureComponent<SingleSelectionControlsProps> {

  public override render(): JSX.Element {
    return (
      <ApolloConsumer>
        {client => <SingleSelectionControlsInternal {...this.props} client={client} />}
      </ApolloConsumer>
    );
  }
}

type SingleSelectionControlsInternalProps = SingleSelectionControlsProps & {
  readonly client: DataCatalogApolloClient;
};

type SingleSelectionControlsInternalState = {
  readonly selecting: boolean;
};

class SingleSelectionControlsInternal extends React.PureComponent<
  SingleSelectionControlsInternalProps,
  SingleSelectionControlsInternalState
> {

  private readonly memoizedGetKeySetSize = defaultMemoize(getKeySetSize);

  public constructor(props: SingleSelectionControlsInternalProps) {
    super(props);
    this.state = {
      selecting: false,
    };
  }

  public override render(): JSX.Element {
    return (
      <span className={clearAndCountClass}>
        {this.renderClearAndCountOrSelecting()}
      </span>
    );
  }

  private renderClearAndCountOrSelecting(): JSX.Element | null {
    if (this.state.selecting) {
      return <SmallInlineFadeInSpinner />;
    } else {
      const selectedItemCount = this.memoizedGetKeySetSize(this.props.selectedKeys);
      if (selectedItemCount > 0) {
        return (
          <>
            <ButtonWidget
              buttonType={ButtonType.LowEmphasis}
              handleClick={this.props.clearSelection}
              style={buttonStyle}
              testId={'clear'}
            >
              {Messages.Clear()}
            </ButtonWidget>
            <span className={dividerClass} />
            <span className={textClass}>
              {Messages.XItemsSelected({
                COUNT: selectedItemCount,
                COUNT_LOC: Formatters.formatCount(selectedItemCount)
              })}
            </span>
          </>
        );
      } else {
        return null;
      }
    }
  }
}
